import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Activity, Plus, Pencil, Trash2, Lock, Unlock, Users, MapPin, Clock, CalendarDays, Ticket, Download } from "lucide-react";
import { toast } from "sonner";

export const STATUS = {
  open: { label: "التسجيل مفتوح", cls: "bg-emerald-100 text-emerald-700" },
  full: { label: "اكتمل العدد", cls: "bg-amber-100 text-amber-800" },
  deadline: { label: "انتهى موعد التسجيل", cls: "bg-slate-200 text-slate-600" },
  closed: { label: "التسجيل مغلق", cls: "bg-red-100 text-red-700" },
  past: { label: "منتهٍ", cls: "bg-slate-300 text-slate-700" },
};

const EMPTY = { title: "", description: "", date: "", time: "", location: "", seats: 0, deadline: "", closed: false };

export function ActivityCard({ a, children, onClick }) {
  const st = STATUS[a.status] || STATUS.open;
  return (
    <div data-testid={`activity-${a.id}`} onClick={onClick} className={`rounded-2xl border border-slate-100 bg-card p-5 soft-shadow card-hover ${onClick ? "cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/10 text-navy"><Activity className="h-6 w-6" /></div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${st.cls}`}>{st.label}</span>
      </div>
      <h3 className="mt-3 font-heading text-lg font-bold text-navy">{a.title}</h3>
      {a.description && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{a.description}</p>}
      <div className="mt-3 space-y-1.5 text-xs text-slate-500">
        {(a.date || a.time) && <p className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> {a.date} {a.time && `— ${a.time}`}</p>}
        {a.location && <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {a.location}</p>}
        <p className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {a.registered_count} مسجّل{a.seats > 0 ? ` من ${a.seats}` : ""}</p>
        {a.deadline && <p className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> آخر موعد للتسجيل: {a.deadline}</p>}
      </div>
      {children}
    </div>
  );
}

export default function Activities() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [tab, setTab] = useState("current");

  const load = (t = tab) => api.get(`/activities${t === "archive" ? "?archived=true" : ""}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { setItems(null); load(tab); /* eslint-disable-next-line */ }, [tab]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ title: a.title, description: a.description || "", date: a.date || "", time: a.time || "", location: a.location || "", seats: a.seats || 0, deadline: a.deadline || "", closed: !!a.closed }); setOpen(true); };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("اسم النشاط مطلوب"); return; }
    const payload = { ...form, seats: Number(form.seats) || 0 };
    try {
      if (editing) await api.put(`/activities/${editing.id}`, payload);
      else await api.post("/activities", payload);
      toast.success(editing ? "تم تحديث النشاط" : "تمت إضافة النشاط");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (a) => { if (!window.confirm("حذف هذا النشاط؟")) return; try { await api.delete(`/activities/${a.id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleClose = async (a) => { try { await api.put(`/activities/${a.id}`, { title: a.title, description: a.description, date: a.date, time: a.time, location: a.location, seats: a.seats, deadline: a.deadline, closed: !a.closed }); toast.success(a.closed ? "تم فتح التسجيل" : "تم إغلاق التسجيل"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const viewParticipants = async (a) => { setParticipants({ activity: a, list: null }); try { const r = await api.get(`/activities/${a.id}/participants`); setParticipants({ activity: a, list: r.data }); } catch (e) { setParticipants({ activity: a, list: [] }); } };

  const register = async (a) => { try { const r = await api.post(`/activities/${a.id}/register`); toast.success(r.data.message); setDetail(null); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const withdraw = async (a) => { try { await api.delete(`/activities/${a.id}/register`); toast.success("تم إلغاء تسجيلك"); setDetail(null); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const exportParticipants = async (a, format = "excel") => {
    try { const res = await api.get(`/activities/${a.id}/participants/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const link = document.createElement("a"); link.href = url; link.download = `participants-${a.title}.${format === "pdf" ? "pdf" : "xlsx"}`; link.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("تعذّر التصدير"); }
  };

  if (items === null) return <PageLoader />;

  return (
    <>
      <PageHeader title="الأنشطة" subtitle={isAdmin ? "إدارة الأنشطة المتاحة للطلاب" : "تصفّح الأنشطة وشارك فيها"}
        action={isAdmin && <Button data-testid="add-activity-btn" onClick={openCreate} className="rounded-xl bg-navy hover:bg-navy-light"><Plus className="me-2 h-4 w-4" /> إضافة نشاط</Button>} />

      <div className="mb-5 inline-flex rounded-xl border border-slate-200 bg-white p-1">
        <button data-testid="tab-current" onClick={() => setTab("current")} className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${tab === "current" ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-50"}`}>الأنشطة الحالية</button>
        <button data-testid="tab-archive" onClick={() => setTab("archive")} className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${tab === "archive" ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-50"}`}>الأرشيف (المنتهية)</button>
      </div>

      {items.length === 0 ? <EmptyState icon={Activity} title={tab === "archive" ? "لا توجد أنشطة منتهية" : "لا توجد أنشطة"} desc={tab === "archive" ? "ستظهر هنا الأنشطة بعد انتهاء موعدها." : (isAdmin ? "أضف أول نشاط." : "لم تتم إضافة أنشطة بعد.")} /> : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => (
            <ActivityCard key={a.id} a={a} onClick={!isAdmin ? () => setDetail(a) : undefined}>
              {isAdmin ? (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  <Button data-testid={`participants-${a.id}`} onClick={() => viewParticipants(a)} size="sm" variant="outline" className="rounded-lg"><Users className="me-1 h-4 w-4" /> المسجّلون ({a.registered_count})</Button>
                  <Button data-testid={`close-${a.id}`} onClick={() => toggleClose(a)} size="sm" variant="outline" className="rounded-lg">{a.closed ? <><Unlock className="me-1 h-4 w-4" /> فتح</> : <><Lock className="me-1 h-4 w-4" /> إغلاق</>}</Button>
                  <Button data-testid={`edit-activity-${a.id}`} onClick={() => openEdit(a)} size="sm" variant="ghost" className="h-8 w-8 rounded-lg p-0 text-slate-500"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`del-activity-${a.id}`} onClick={() => remove(a)} size="sm" variant="ghost" className="h-8 w-8 rounded-lg p-0 text-red-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ) : a.is_registered && <p className={`mt-3 border-t border-slate-100 pt-3 text-xs font-semibold ${a.reg_status === "waitlist" ? "text-amber-600" : "text-emerald-600"}`}>{a.reg_status === "waitlist" ? "⏳ أنت في قائمة الانتظار" : "✓ أنت مسجّل في هذا النشاط"}</p>}
            </ActivityCard>
          ))}
        </div>
      )}

      {/* Student detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="activity-detail">
          {detail && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading text-navy"><Activity className="h-5 w-5 text-navy" /> {detail.title}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${(STATUS[detail.status] || STATUS.open).cls}`}>{(STATUS[detail.status] || STATUS.open).label}</span>
              {detail.description && <p className="text-sm text-slate-600">{detail.description}</p>}
              <div className="space-y-2 rounded-xl bg-slate-50 p-4 text-sm">
                <Row icon={CalendarDays} label="التاريخ والوقت" value={`${detail.date || "—"} ${detail.time ? `— ${detail.time}` : ""}`} />
                <Row icon={MapPin} label="المكان" value={detail.location || "—"} />
                <Row icon={Users} label="المقاعد" value={detail.seats > 0 ? `${detail.registered_count} / ${detail.seats}` : `${detail.registered_count} (غير محدود)`} />
                <Row icon={Clock} label="آخر موعد للتسجيل" value={detail.deadline || "—"} />
              </div>
              {user.role === "student" && (
                detail.is_registered ? (
                  <div className="space-y-2">
                    {detail.reg_status === "waitlist" && <p className="rounded-lg bg-amber-50 p-2 text-center text-xs font-semibold text-amber-700">أنت في قائمة الانتظار — سيتم تأكيد تسجيلك عند توفّر مقعد</p>}
                    <Button data-testid="withdraw-btn" onClick={() => withdraw(detail)} variant="outline" className="w-full rounded-xl border-red-200 text-red-600 hover:bg-red-50">إلغاء التسجيل</Button>
                  </div>
                ) : detail.closed ? (
                  <Button data-testid="register-btn" disabled className="w-full rounded-xl bg-slate-200 py-6 text-slate-500">التسجيل غير متاح</Button>
                ) : detail.is_full ? (
                  <Button data-testid="waitlist-btn" onClick={() => register(detail)} className="w-full rounded-xl bg-amber-500 py-6 text-white hover:bg-amber-600"><Ticket className="me-2 h-4 w-4" /> الانضمام لقائمة الانتظار</Button>
                ) : (
                  <Button data-testid="register-btn" onClick={() => register(detail)} className="w-full rounded-xl bg-greenx py-6 hover:bg-greenx-light"><Ticket className="me-2 h-4 w-4" /> المشاركة في النشاط</Button>
                )
              )}
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Admin create/edit dialog */}
      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-navy">{editing ? "تعديل النشاط" : "إضافة نشاط"}</DialogTitle></DialogHeader>
            <div className="grid gap-3 py-2">
              <div><Label>اسم النشاط</Label><Input data-testid="act-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1.5 rounded-lg" /></div>
              <div><Label>الوصف</Label><Textarea data-testid="act-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} className="mt-1.5 rounded-lg" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>التاريخ</Label><Input data-testid="act-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="mt-1.5 rounded-lg" /></div>
                <div><Label>الوقت</Label><Input data-testid="act-time" type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className="mt-1.5 rounded-lg" /></div>
              </div>
              <div><Label>المكان</Label><Input data-testid="act-location" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="mt-1.5 rounded-lg" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>عدد المقاعد (0 = غير محدود)</Label><Input data-testid="act-seats" type="number" min="0" value={form.seats} onChange={(e) => setForm((f) => ({ ...f, seats: e.target.value }))} className="mt-1.5 rounded-lg" /></div>
                <div><Label>آخر موعد للتسجيل</Label><Input data-testid="act-deadline" type="date" value={form.deadline} onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))} className="mt-1.5 rounded-lg" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="act-closed" type="checkbox" checked={form.closed} onChange={(e) => setForm((f) => ({ ...f, closed: e.target.checked }))} /> إغلاق التسجيل</label>
            </div>
            <DialogFooter><Button data-testid="act-save" onClick={submit} disabled={!form.title.trim()} className="rounded-xl bg-navy hover:bg-navy-light">حفظ</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Participants dialog */}
      <Dialog open={!!participants} onOpenChange={(o) => !o && setParticipants(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto" data-testid="participants-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 font-heading text-navy">
              <span>المسجّلون في «{participants?.activity?.title}»</span>
              {participants?.list?.length > 0 && (
                <div className="flex gap-2">
                  <Button data-testid="export-participants" onClick={() => exportParticipants(participants.activity, "excel")} size="sm" variant="outline" className="rounded-lg"><Download className="me-1 h-4 w-4" /> Excel</Button>
                  <Button data-testid="export-participants-pdf" onClick={() => exportParticipants(participants.activity, "pdf")} size="sm" variant="outline" className="rounded-lg"><Download className="me-1 h-4 w-4" /> PDF</Button>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          {!participants?.list ? <div className="py-6 text-center text-slate-400">جارٍ التحميل...</div>
            : participants.list.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">لا يوجد مسجّلون بعد</p>
            : <div className="space-y-2">{participants.list.map((p, i) => (
                <div key={p.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2.5 text-sm">
                  <span className="font-semibold text-navy">{i + 1}. {p.name}</span>
                  <div className="flex items-center gap-2">
                    {p.status === "waitlist" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">قائمة انتظار</span>}
                    <span className="text-xs text-slate-400">{p.phone || p.email || ""}</span>
                  </div>
                </div>))}</div>}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Row({ icon: Icon, label, value }) {
  return <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-slate-400"><Icon className="h-4 w-4" /> {label}</span><span className="font-semibold text-navy">{value}</span></div>;
}
